<template>
  <div>
    <head>
      <link rel="shortcut icon" href="assets/images/favicon.svg">
      <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
      <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
    </head>

    <body @contextmenu.prevent data-sidebar="dark">
      <div id="layout-wrapper">
        <NavigationBar />

        <div id="content" class="content-minimize">
          <div class="page-content">
            <div class="container-fluid">

              <!-- Search Section -->
              <div class="row justify-content-center">
                <div class="col-lg-10 col-xl-8">
                  
                  <!-- Main Search Card -->
                  <div class="card search-card mb-4">
                    <div class="card-body p-4">
                      <!-- Search Header -->
                      <div class="d-flex justify-content-between align-items-center mb-4">
                        <div>
                          <h5 class="mb-1"><i class="mdi mdi-account-search me-2 text-primary"></i>Patient Search</h5>
                          <small class="text-muted">Search by name, ID number, phone, or patient number</small>
                        </div>
                        <button class="btn btn-primary" @click="goTo('patient-register')">
                          <i class="mdi mdi-account-plus me-1"></i> New Patient
                        </button>
                      </div>

                      <!-- Search Input -->
                      <div class="search-input-wrapper">
                        <div class="input-group input-group-lg search-box">
                          <span class="input-group-text">
                            <i v-if="loading" class="mdi mdi-loading mdi-spin text-primary"></i>
                            <i v-else class="mdi mdi-magnify text-muted"></i>
                          </span>
                          <input 
                            type="text" 
                            class="form-control"
                            v-model="searchTerm"
                            placeholder="Type patient name, ID number, phone..."
                            ref="searchInput"
                            @keydown.esc="clearSearch"
                          >
                          <button v-if="searchTerm" class="btn btn-light border" @click="clearSearch" type="button">
                            <i class="mdi mdi-close"></i>
                          </button>
                        </div>
                        <div class="search-hints mt-2">
                          <small v-if="searchTerm && searchTerm.length < 2" class="text-warning">
                            <i class="mdi mdi-information me-1"></i>Type at least 2 characters to search
                          </small>
                          <small v-else-if="!searchTerm" class="text-muted">
                            <i class="mdi mdi-lightbulb-outline me-1"></i>
                            Try: <span class="search-example">John Doe</span>, <span class="search-example">12345678</span>, <span class="search-example">0712345678</span>, <span class="search-example">PT2601...</span>
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Results Section -->
                  <div v-if="loading && !results.length" class="text-center py-5">
                    <div class="spinner-border text-primary mb-3"></div>
                    <p class="text-muted mb-0">Searching patients...</p>
                  </div>

                  <!-- No Results -->
                  <div v-else-if="hasSearched && results.length === 0" class="card">
                    <div class="card-body text-center py-5">
                      <div class="empty-state-icon mb-3">
                        <i class="mdi mdi-account-search-outline"></i>
                      </div>
                      <h5 class="mb-2">No patients found</h5>
                      <p class="text-muted mb-4">No results for "<strong>{{ searchTerm }}</strong>". Try a different search term or register the patient.</p>
                      <div class="d-flex justify-content-center gap-2">
                        <button class="btn btn-outline-primary" @click="clearSearch">
                          <i class="mdi mdi-magnify me-1"></i> New Search
                        </button>
                        <button class="btn btn-success" @click="goTo('patient-register')">
                          <i class="mdi mdi-account-plus me-1"></i> Register Patient
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Results List -->
                  <div v-else-if="results.length > 0">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                      <span class="text-muted">
                        <i class="mdi mdi-check-circle text-success me-1"></i>
                        Found <strong>{{ totalCount }}</strong> patient{{ totalCount !== 1 ? 's' : '' }}
                      </span>
                    </div>

                    <!-- Patient Cards -->
                    <div class="patient-results">
                      <div 
                        v-for="patient in results" 
                        :key="patient.patientID" 
                        class="card patient-result-card mb-2"
                        @click="viewPatient(patient)"
                      >
                        <div class="card-body py-3">
                          <div class="row align-items-center">
                            <div class="col-auto">
                              <div class="avatar-md">
                                <span class="avatar-title rounded-circle" :class="patient.gender === 'Male' ? 'bg-primary' : 'bg-pink'">
                                  {{ getInitials(patient.fullNames) }}
                                </span>
                              </div>
                            </div>
                            <div class="col">
                              <h6 class="mb-1">{{ patient.fullNames }}</h6>
                              <div class="d-flex flex-wrap gap-2 text-muted small">
                                <span><i class="mdi mdi-pound me-1"></i>{{ patient.patientID }}</span>
                                <span v-if="patient.idNo"><i class="mdi mdi-card-account-details me-1"></i>{{ patient.idNo }}</span>
                                <span v-if="patient.phoneNumber"><i class="mdi mdi-phone me-1"></i>{{ patient.phoneNumber }}</span>
                              </div>
                            </div>
                            <div class="col-auto text-end d-none d-md-block">
                              <span class="badge me-2" :class="patient.gender === 'Male' ? 'bg-primary-subtle text-primary' : 'bg-pink-subtle text-pink'">
                                {{ patient.gender }}
                              </span>
                              <span class="badge bg-light text-dark">{{ patient.age }} yrs</span>
                            </div>
                            <div class="col-auto">
                              <div class="d-flex gap-2">
                                <button class="btn btn-sm btn-soft-primary" @click.stop="viewPatient(patient)" title="View Profile">
                                  <i class="mdi mdi-eye"></i>
                                </button>
                                <button class="btn btn-sm btn-soft-success" @click.stop="createVisit(patient)" title="New Visit">
                                  <i class="mdi mdi-calendar-plus"></i>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Pagination -->
                    <nav v-if="totalPages > 1" class="mt-4">
                      <ul class="pagination justify-content-center mb-0">
                        <li class="page-item" :class="{ disabled: currentPage === 1 }">
                          <a class="page-link" href="#" @click.prevent="goToPage(currentPage - 1)"><i class="mdi mdi-chevron-left"></i></a>
                        </li>
                        <li v-for="page in visiblePages" :key="page" class="page-item" :class="{ active: page === currentPage }">
                          <a class="page-link" href="#" @click.prevent="goToPage(page)">{{ page }}</a>
                        </li>
                        <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                          <a class="page-link" href="#" @click.prevent="goToPage(currentPage + 1)"><i class="mdi mdi-chevron-right"></i></a>
                        </li>
                      </ul>
                    </nav>
                  </div>

                  <!-- Initial State: Quick Actions -->
                  <div v-else-if="!hasSearched">
                    <!-- Recent Patients -->
                    <div v-if="recentPatients.length > 0" class="card mb-3">
                      <div class="card-header bg-white py-3">
                        <h6 class="mb-0"><i class="mdi mdi-history me-2 text-primary"></i>Recently Viewed Patients</h6>
                      </div>
                      <div class="card-body">
                        <div class="row g-2">
                          <div 
                            v-for="patient in recentPatients" 
                            :key="patient.patientID" 
                            class="col-md-6 col-lg-4"
                          >
                            <div class="recent-patient-card" @click="viewPatient(patient)">
                              <div class="d-flex align-items-center">
                                <div class="avatar-sm me-3">
                                  <span class="avatar-title rounded-circle" :class="patient.gender === 'Male' ? 'bg-primary' : 'bg-pink'">
                                    {{ getInitials(patient.fullNames) }}
                                  </span>
                                </div>
                                <div class="flex-grow-1 overflow-hidden">
                                  <h6 class="mb-0 text-truncate">{{ patient.fullNames }}</h6>
                                  <small class="text-muted">{{ patient.patientID }}</small>
                                </div>
                                <i class="mdi mdi-chevron-right text-muted"></i>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Quick Links -->
                    <div class="row g-3">
                      <div class="col-md-6">
                        <div class="card quick-action-card h-100" @click="goTo('patient-list')">
                          <div class="card-body d-flex align-items-center py-3">
                            <div class="quick-action-icon bg-primary-subtle text-primary me-3">
                              <i class="mdi mdi-format-list-bulleted"></i>
                            </div>
                            <div>
                              <h6 class="mb-0">View All Patients</h6>
                              <small class="text-muted">Browse complete registry</small>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="card quick-action-card h-100" @click="goTo('reception')">
                          <div class="card-body d-flex align-items-center py-3">
                            <div class="quick-action-icon bg-success-subtle text-success me-3">
                              <i class="mdi mdi-account-clock"></i>
                            </div>
                            <div>
                              <h6 class="mb-0">Reception Queue</h6>
                              <small class="text-muted">View waiting patients</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>

            </div>
          </div>

          <footer class="footer">
            <div class="container-fluid">
              <div class="row">
                <div class="col-sm-6"></div>
                <div class="col-sm-6">
                  <div class="text-sm-end d-sm-block">County Hospital Management System</div>
                </div>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </body>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import { execute, hospital } from "@/api";

export default {
  name: "PatientSearch",
  components: { NavigationBar },

  data() {
    return {
      loading: false,
      searchTerm: '',
      searchTimeout: null,
      results: [],
      totalCount: 0,
      currentPage: 1,
      perPage: 10,
      hasSearched: false,
      recentPatients: []
    };
  },

  computed: {
    totalPages() { return Math.ceil(this.totalCount / this.perPage); },
    visiblePages() {
      const pages = [];
      const start = Math.max(1, this.currentPage - 2);
      const end = Math.min(this.totalPages, this.currentPage + 2);
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    }
  },

  watch: {
    searchTerm(val) {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      
      if (!val || val.trim().length < 2) {
        this.results = [];
        this.hasSearched = false;
        return;
      }
      
      this.searchTimeout = setTimeout(() => {
        this.search();
      }, 300);
    }
  },

  mounted() { 
    this.$refs.searchInput?.focus();
    this.loadRecentPatients();
  },

  beforeUnmount() {
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
  },

  methods: {
    goTo(route) { this.$router.push('/' + route); },

    search() {
      if (!this.searchTerm.trim() || this.searchTerm.trim().length < 2) return;
      this.loading = true;
      this.hasSearched = true;
      
      const data = new FormData();
      data.append("function", "searchPatient");
      data.append("search", this.searchTerm.trim());
      data.append("searchMode", "all");
      data.append("page", this.currentPage);
      data.append("limit", this.perPage);
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));

      execute(data, hospital).then((res) => {
        this.loading = false;
        if (res.data.success) {
          this.results = res.data.data.patients || [];
          this.totalCount = res.data.data.totalCount || this.results.length;
        } else {
          this.results = [];
          this.totalCount = 0;
        }
      }).catch((e) => {
        this.loading = false;
        this.results = [];
        this.totalCount = 0;
        console.error('Search error:', e);
      });
    },

    clearSearch() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTerm = '';
      this.results = [];
      this.hasSearched = false;
      this.currentPage = 1;
      this.$refs.searchInput?.focus();
    },

    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.search();
      }
    },

    viewPatient(patient) { 
      this.saveRecentPatient(patient);
      this.$router.push('/patient-profile/' + patient.patientID); 
    },
    
    createVisit(patient) { 
      this.saveRecentPatient(patient);
      this.$router.push('/new-visit?patientID=' + patient.patientID); 
    },

    getInitials(name) {
      if (!name) return '?';
      return name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
    },

    loadRecentPatients() {
      const saved = localStorage.getItem('recentPatients');
      if (saved) {
        try {
          this.recentPatients = JSON.parse(saved).slice(0, 5);
        } catch (e) {
          this.recentPatients = [];
        }
      }
    },

    saveRecentPatient(patient) {
      let recent = this.recentPatients.filter(p => p.patientID !== patient.patientID);
      recent.unshift({ patientID: patient.patientID, fullNames: patient.fullNames, gender: patient.gender });
      recent = recent.slice(0, 5);
      this.recentPatients = recent;
      localStorage.setItem('recentPatients', JSON.stringify(recent));
    }
  }
}
</script>

<style scoped>
/* Search Card */
.search-card {
  border: none;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
}

/* Search Box */
.search-box {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.2s ease;
}

.search-box:focus-within {
  border-color: #556ee6;
  box-shadow: 0 0 0 3px rgba(85, 110, 230, 0.1);
}

.search-box .input-group-text {
  background: #fff;
  border: none;
  padding-left: 1rem;
}

.search-box .form-control {
  border: none;
  font-size: 1rem;
  padding: 0.75rem 0.5rem;
}

.search-box .form-control:focus {
  box-shadow: none;
}

.search-box .btn {
  border-radius: 0;
}

/* Search Hints */
.search-hints {
  min-height: 24px;
}

.search-example {
  background: #f0f2f5;
  padding: 2px 8px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.8rem;
}

/* Avatar Styles */
.avatar-xs { width: 1.5rem; height: 1.5rem; font-size: 0.65rem; }
.avatar-sm { width: 2.5rem; height: 2.5rem; }
.avatar-md { width: 3rem; height: 3rem; }
.avatar-title { 
  display: flex; 
  align-items: center; 
  justify-content: center; 
  width: 100%; 
  height: 100%; 
  font-weight: 600; 
  color: #fff;
}

/* Colors */
.bg-primary-subtle { background-color: rgba(85, 110, 230, 0.15); }
.bg-pink-subtle { background-color: rgba(232, 62, 140, 0.15); }
.bg-success-subtle { background-color: rgba(52, 195, 143, 0.15); }
.bg-pink { background-color: #e83e8c !important; }
.text-pink { color: #e83e8c !important; }

/* Patient Result Card */
.patient-result-card {
  cursor: pointer;
  border: 1px solid #e9ecef;
  transition: all 0.2s ease;
}

.patient-result-card:hover {
  border-color: #556ee6;
  box-shadow: 0 4px 12px rgba(85, 110, 230, 0.15);
  transform: translateX(4px);
}

/* Recent Patient Card */
.recent-patient-card {
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.recent-patient-card:hover {
  background: #e9ecef;
}

/* Quick Action Card */
.quick-action-card {
  cursor: pointer;
  border: 1px solid #e9ecef;
  transition: all 0.2s ease;
}

.quick-action-card:hover {
  border-color: #556ee6;
  background: #f8f9ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
}

.quick-action-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.quick-action-icon i {
  font-size: 20px;
}

/* Empty State */
.empty-state-icon {
  width: 80px;
  height: 80px;
  background: #f8f9fa;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

.empty-state-icon i {
  font-size: 40px;
  color: #adb5bd;
}

/* Buttons */
.btn-soft-primary { 
  background-color: rgba(85, 110, 230, 0.1); 
  color: #556ee6; 
  border: none; 
}
.btn-soft-primary:hover { 
  background-color: #556ee6; 
  color: #fff; 
}

.btn-soft-success { 
  background-color: rgba(52, 195, 143, 0.1); 
  color: #34c38f; 
  border: none; 
}
.btn-soft-success:hover { 
  background-color: #34c38f; 
  color: #fff; 
}
</style>
